/**
 * Centralized localStorage utility
 * Provides type-safe, consistent access to localStorage with error handling
 */

const KEYS = {
  ACCESS_TOKEN: "access_token",
  USER_ID: "user_id",
  USER_TYPE: "user_type",
  USER_FIRST_NAME: "user_first_name",
  USER_LAST_NAME: "user_last_name",
  REFRESH_TOKEN: "refresh-token",
  EMAIL: "email",
  PASSWORD: "password",
};

/**
 * Generic storage operations
 */
export const storage = {
  /**
   * Get item from localStorage
   * Attempts to parse JSON, falls back to string
   */
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;

      // Try to parse as JSON, fall back to string
      try {
        return JSON.parse(item);
      } catch {
        return item;
      }
    } catch (error) {
      console.error(`Error reading from localStorage (${key}):`, error);
      return null;
    }
  },

  /**
   * Set item in localStorage
   * Automatically serializes objects to JSON
   */
  set: (key, value) => {
    try {
      const serialized =
        typeof value === "string" ? value : JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      console.error(`Error saving to localStorage (${key}):`, error);
      return false;
    }
  },

  /**
   * Remove item from localStorage
   */
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
      return false;
    }
  },

  /**
   * Clear all items from localStorage
   */
  clear: () => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error("Error clearing localStorage:", error);
      return false;
    }
  },

  /**
   * Clear all user-related data
   * Removes authentication and user data keys (excludes email/password for remember me)
   */
  clearUserData: () => {
    const authKeys = [
      KEYS.ACCESS_TOKEN,
      KEYS.USER_ID,
      KEYS.USER_TYPE,
      KEYS.USER_FIRST_NAME,
      KEYS.USER_LAST_NAME,
      KEYS.REFRESH_TOKEN,
    ];
    authKeys.forEach((key) => storage.remove(key));
  },

  /**
   * Clear authentication data but keep email/password for remember me
   */
  clearAuthData: () => {
    storage.remove(KEYS.ACCESS_TOKEN);
    storage.remove(KEYS.REFRESH_TOKEN);
  },
};

/**
 * Token-specific methods
 */
export const tokenStorage = {
  getAccessToken: () => storage.get(KEYS.ACCESS_TOKEN),
  setAccessToken: (token) => storage.set(KEYS.ACCESS_TOKEN, token),
  removeAccessToken: () => storage.remove(KEYS.ACCESS_TOKEN),

  getRefreshToken: () => storage.get(KEYS.REFRESH_TOKEN),
  setRefreshToken: (token) => storage.set(KEYS.REFRESH_TOKEN, token),
  removeRefreshToken: () => storage.remove(KEYS.REFRESH_TOKEN),
};

/**
 * User data methods
 */
export const userStorage = {
  /**
   * Get user object with all user-related data
   */
  getUser: () => ({
    id: storage.get(KEYS.USER_ID),
    type: storage.get(KEYS.USER_TYPE),
    firstName: storage.get(KEYS.USER_FIRST_NAME),
    lastName: storage.get(KEYS.USER_LAST_NAME),
  }),

  /**
   * Set user data (accepts partial user object)
   */
  setUser: (user) => {
    if (!user || typeof user !== "object") return false;

    if (user.id !== undefined) storage.set(KEYS.USER_ID, user.id);
    if (user.type !== undefined) storage.set(KEYS.USER_TYPE, user.type);
    if (user.firstName !== undefined)
      storage.set(KEYS.USER_FIRST_NAME, user.firstName);
    if (user.lastName !== undefined)
      storage.set(KEYS.USER_LAST_NAME, user.lastName);

    return true;
  },

  /**
   * Remove all user data
   */
  removeUser: () => {
    storage.remove(KEYS.USER_ID);
    storage.remove(KEYS.USER_TYPE);
    storage.remove(KEYS.USER_FIRST_NAME);
    storage.remove(KEYS.USER_LAST_NAME);
  },

  /**
   * Get individual user properties
   */
  getUserId: () => storage.get(KEYS.USER_ID),
  getUserType: () => storage.get(KEYS.USER_TYPE),
  getUserFirstName: () => storage.get(KEYS.USER_FIRST_NAME),
  getUserLastName: () => storage.get(KEYS.USER_LAST_NAME),

  /**
   * Set individual user properties
   */
  setUserId: (id) => storage.set(KEYS.USER_ID, id),
  setUserType: (type) => storage.set(KEYS.USER_TYPE, type),
  setUserFirstName: (firstName) => storage.set(KEYS.USER_FIRST_NAME, firstName),
  setUserLastName: (lastName) => storage.set(KEYS.USER_LAST_NAME, lastName),
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!tokenStorage.getAccessToken();
};

/**
 * Clear all authentication and user data
 */
export const clearAuth = () => {
  storage.clearUserData();
};

/**
 * Remember me credentials storage
 */
export const rememberMeStorage = {
  getEmail: () => storage.get(KEYS.EMAIL),
  setEmail: (email) => storage.set(KEYS.EMAIL, email),
  removeEmail: () => storage.remove(KEYS.EMAIL),

  getPassword: () => storage.get(KEYS.PASSWORD),
  setPassword: (password) => storage.set(KEYS.PASSWORD, password),
  removePassword: () => storage.remove(KEYS.PASSWORD),

  setCredentials: (email, password) => {
    storage.set(KEYS.EMAIL, email);
    storage.set(KEYS.PASSWORD, password);
  },

  removeCredentials: () => {
    storage.remove(KEYS.EMAIL);
    storage.remove(KEYS.PASSWORD);
  },

  getCredentials: () => ({
    email: storage.get(KEYS.EMAIL),
    password: storage.get(KEYS.PASSWORD),
  }),
};

// Export KEYS for direct access if needed
export { KEYS };

// Default export with all utilities
const localStorageUtils = {
  storage,
  tokenStorage,
  userStorage,
  rememberMeStorage,
  isAuthenticated,
  clearAuth,
  KEYS,
};

export default localStorageUtils;
